/**
 * Component-bearing constants.
 *
 * These live apart from `src/constants/index.tsx` on purpose: they embed React
 * components (`InviteUser`, `Icon`), which would otherwise pull the rbac UI and
 * openui `Select` into every consumer of the constants barrel — including eager
 * modules (posthog.config, ThemeProvider, AuthContext) that only need plain
 * string/API constants. Keeping them here means importing `@/constants` no longer
 * drags that component graph into the entry chunk.
 */
import { Share2 } from "lucide-react";
import { Gemini } from "@/components/icons/Gemini";
import { Grok } from "@/components/icons/Grok";
import { OpenAI } from "@/components/icons/OpenAI";
import { InviteUser } from "@/components/rbac/InviteUser";
import { Icon } from "@/components/ui/icon";
import type { DialogOptions } from "@/contexts/DialogContext";
import type { SubscriptionData } from "@/helpers/subscription.helper";
import type { McpGuideClient } from "@/constants";

export const ALERTS = {
  SUBSCRIPTION: {
    TRIAL_EXPIRED: {
      title: 'Your Saras iQ trial has expired',
      desc: 'Upgrade to keep using Saras iQ and unlock full access.',
      className: '',
      action: [{label: 'Talk to Us', id: 'TALK_TO_US', type: 'primary'}],
      icon: <Icon icon="hourglass.png" className="m-2 w-20"/>,
      iconClass: ''
    } as SubscriptionData['dialog'],
    ENTERPRISE_EXPIRED: {
      title: 'Your Enterprise plan has been cancelled',
      desc: 'Resubscribe to keep using Saras iQ and retain full access.',
      className: '',
      action: [{label: 'Talk to Us', id: 'TALK_TO_US', type: 'primary'}],
      icon: <Icon icon="causion.png" className="m-2 w-20"/>,
      iconClass: ''
    } as SubscriptionData['dialog'],
    CREDITS_EXPIRED: {
      title: `You've used all your credits.`,
      desc: 'Upgrade your plan to continue using Saras iQ without interruption',
      className: '',
      action: [{label: 'Talk to Us', id: 'TALK_TO_US', type: 'primary'}],
      icon: <Icon icon="causion.png" className="m-2 w-20"/>,
      iconClass: ''
    } as SubscriptionData['dialog'],
    TRIAL_STARTED: {
      title: `🎉 Welcome to Saras iQ!`,
      desc: 'Your 31-day free trial has started, and 100 free credits have been added to your account.',
      className: '',
      action: [{label: 'Ask iQ', id: 'ASK_IQ', type: 'primary'}],
      icon: '',
      iconClass: ''
    } as SubscriptionData['dialog']
  },
  FEEDBACK: {
    REPORT_ISSUE_LOADING: {
      TITLE: 'Submitting your request…',
    },
    REPORT_ISSUE_SUCCESS: {
      TITLE: 'Your request has been submitted successfully.',
      DESCRIPTION: 'Our team will review it and aim to resolve it within up to 2 weeks.',
    },
    REPORT_ISSUE_ERROR: {
      TITLE: 'Something went wrong. Please try again.',
    },
  },
  USER_MANAGEMENT: {
    INVITE_USER:{
      title: 'Invite user',
      desc: <InviteUser />,
      action: [],
      cancelText: 'Cancel',
    } as DialogOptions,
    DELETE_USER: {
      title: 'Delete user?',
      desc: 'Are you sure you want to delete this user? This action cannot be undone, and the user will lose all access to the platform.',
      action: [
        {
          type: 'ghost',
          label: 'Cancel',
          id: 'CANCEL'
        },
        {
          type: 'destructiveFilled',
          label: 'Delete User',
          id: 'DELETE_USER'
        }
      ],
      cancelText: 'Cancel',
    } as DialogOptions,
  },
  // Manage Group's delete confirmation moved to a real AlertDialog
  // (ManageGroup.tsx) instead of the generic showDialog this constant fed —
  // a destructive, hard-to-reverse action gets AlertDialog's stricter
  // defaults (no overlay dismiss, focus on Cancel, role="alertdialog").
  ACTIONS: {
    CANCEL: 'CANCEL',
    TALK_TO_US: 'TALK_TO_US',
    ASK_IQ: 'ASK_IQ',
    INVITE_USER: 'INVITE_USER',
    DELETE_USER: 'DELETE_USER'
  }
}

export const CHAT_SHARE_DIALOG: Omit<DialogOptions, "onConfirm" | "onCancel"> = {
  title: (
    <span className="flex items-center gap-2">
      <Share2 className="h-4 w-4 text-[color:var(--dash-ink-2)]" />
      Share chat
    </span>
  ),
  action: [],
};

export const SETTINGS = {
  INTEGRATIONS: {
    title: 'Integrations',
    description: 'Connect your favorite tools and services to enhance your Saras IQ experience.',
    SLACK: {
      name: 'iQ Data Analyst',
      description: 'Ask business questions in Slack and get answers powered by Saras iQ.',
      // Same mark and size the IQ Data Engineer row renders — both cards install
      // a Slack app, so the two rows must line up pixel for pixel.
      icon: <Icon icon="slack.svg" size={24} />
    },
    PULSE: {
      name: 'iQ Data Engineer',
      description: 'Bring the Saras data engineering agent into Slack to explore your data dictionary, trace data lineage, and monitor data freshness.',
      // Both cards install a Slack app, so the row carries the Slack mark for
      // now — same icon and size the Slack row renders.
      icon: <Icon icon="slack.svg" size={24} />
    },
    // The Integrations › Apps rows for the MCP clients, keyed by client (row
    // order and guide routes come from `MCP_GUIDE_CLIENTS` / `ROUTES.SETTINGS_MCP_GUIDES`).
    // `siteUrl` is the client's own website, linked from its guide's first step.
    // The MCP endpoint itself is `MCP_SERVER_URL` in `@/constants`.
    MCP_CLIENTS: {
      claude: {
        name: 'Claude (MCP)',
        description: 'Connect Saras iQ (Data Analyst) to Claude and get AI-powered answers to your business questions.',
        icon: <Icon icon="claude.svg" size={24} />
      },
      codex: {
        name: 'Codex (MCP)',
        description: 'Connect Saras iQ (Data Analyst) to ChatGPT and Codex and get AI-powered answers to your business questions.',
        siteUrl: 'https://chatgpt.com',
        // Codex is an OpenAI product, so the row uses the OpenAI mark.
        icon: <OpenAI className="size-6 text-[color:var(--dash-ink,var(--text-high-emphasis))]" />
      },
      grok: {
        name: 'Grok (MCP)',
        description: 'Connect Saras iQ (Data Analyst) to Grok and get AI-powered answers to your business questions.',
        siteUrl: 'https://grok.com',
        connectorsUrl: 'https://grok.com/connectors',
        icon: <Grok className="size-6 text-[color:var(--dash-ink,var(--text-high-emphasis))]" />
      },
      gemini: {
        name: 'Gemini (MCP)',
        description: 'Connect Saras iQ (Data Analyst) to Gemini and get AI-powered answers to your business questions.',
        siteUrl: 'https://gemini.google.com',
        customizeUrl: 'https://gemini.google.com/customize',
        icon: <Gemini className="size-6" />
      },
    } satisfies Record<McpGuideClient, { name: string; description: string; icon: React.ReactNode; siteUrl?: string; connectorsUrl?: string; customizeUrl?: string }>,
    TEAMS: {
      name: 'Microsoft Teams',
      description: 'Ask business questions directly in Microsoft Teams and get answers powered by Saras iQ.',
      icon: <Icon icon="teams.svg" size={24} />
    }
  },
}
